import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { CopyButton } from "../components/CopyButton";

// ---------------------------------------------------------------- content

const WP_TASKS = [
  {
    n: "T1",
    title: "Researcher journey & access-friction mapping",
    body: "Map how researchers discover, request and access data — including delays, uncertainty, duplicated forms and unclear approval routes.",
  },
  {
    n: "T2",
    title: "Role and authority model",
    body: "Define the roles involved: researcher, data depositor, data service, data owner, ethics reviewer, TRE operator, access approver and support staff.",
  },
  {
    n: "T3",
    title: "Bounded AI / SLM discovery assistant",
    body: "Design AI assistance for dataset discovery, access guidance, metadata gaps and synthetic preview options. Prefer small or specialist models where appropriate, with clear human decision points.",
  },
  {
    n: "T4",
    title: "Receipt-backed access workflow",
    body: "Create role-to-role workflows that record purpose, authority, evidence reference, dependency, access decision and outcome — without exposing raw sensitive data.",
  },
  {
    n: "T5",
    title: "dtool / devtool lifecycle governance",
    body: "Support controlled change from requirements and source through CI/CD, prompt / model configuration, dependency tracking, workflow updates and impact-aware reporting.",
  },
  {
    n: "T6",
    title: "Upskilling and adoption pack",
    body: "Training materials, role guides, staff playbooks and governance patterns for data-service teams adopting new technology.",
  },
];

const PARTNERS = [
  "UK Data Service / UK Data Archive",
  "ADR UK / ONS / ADR national services",
  "Healthy and Sustainable Places Data Service",
  "UK Longitudinal Linkage Collaboration",
  "CLOSER / CELSIUS",
  "Population Research UK",
  "Smart Data Donation Service",
  "Smart Energy Data Service",
  "Geographic Data Service",
];

const GOOD_FIT = [
  "The opportunity involves fragmented evidence, sensitive data, researcher access journeys, AI-assisted discovery, workflow automation, staff upskilling or federated access.",
  "Existing infrastructure needs enhancement rather than replacement.",
  "Roles, authority and approvals need to be clearer.",
  "There is a need to reduce administrative friction and improve measurable reporting.",
  "KATLAS can lead a defined work package, not merely provide a small tool.",
];

const WEAK_FIT = [
  "The project seeks a new standalone data infrastructure.",
  "The problem is only generic project management or dissemination.",
  "There is no multi-party evidence, access or authority challenge.",
  "KATLAS is treated only as a supplier after the governance design is fixed.",
];

const SUMMARY_TEXT = [
  "KATLAS — ESRC Future Data Services: receipt-backed federated access",
  "",
  "A governed access model for improving discovery, permissioned use and evidence-led coordination across existing ESRC social science data service infrastructures. KATLAS enhances existing services — it does not propose a new data infrastructure.",
  "",
  "Proposed work package: Receipt-backed Federated Access and Governed AI Researcher Journeys",
  "",
  "Objective: reduce friction in discovery and sensitive-data access by governing the researcher journey across existing ESRC data services, while keeping sensitive data local, bounding AI/tool use, supporting staff adoption, and producing signed receipts for access decisions, dependencies and outcomes.",
  "",
  ...WP_TASKS.map((t) => `${t.n} ${t.title}\n${t.body}`),
  "",
  "Potential infrastructure partners (illustrative — no participation implied):",
  ...PARTNERS.map((p) => `- ${p}`),
  "",
  "Good fit if:",
  ...GOOD_FIT.map((g) => `- ${g}`),
  "",
  "Weak fit if:",
  ...WEAK_FIT.map((w) => `- ${w}`),
  "",
  "AI assists. Roles decide. Receipts prove.",
  "",
  "https://platform.katlastechnology.com/fit/esrc-future-data-services",
].join("\n");

// ---------------------------------------------------------------- atoms

const Card = ({ n, title, body, accent = "blue", testId }) => (
  <article
    data-testid={testId}
    className="bg-[#020B14] border border-white/[0.08] p-5 sm:p-6"
  >
    <div className="flex items-baseline gap-3 mb-2">
      <span
        className={`mono text-[10px] uppercase tracking-[0.22em] ${
          accent === "amber" ? "text-[#EAB308]" : "text-[#0066FF]"
        }`}
      >
        {n}
      </span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
    <h3 className="font-display text-[16px] sm:text-lg text-white leading-snug">
      {title}
    </h3>
    <p className="mt-2.5 text-sm text-slate-300 leading-relaxed">{body}</p>
  </article>
);

const FitColumn = ({ tone, label, items, testId }) => {
  const headerCls =
    tone === "good"
      ? "text-[#0066FF] border-[#0066FF]/40"
      : "text-[#EAB308] border-amber-500/40";
  const dot = tone === "good" ? "bg-[#0066FF]" : "bg-[#EAB308]";
  return (
    <div
      data-testid={testId}
      className={`bg-[#020B14] border ${
        tone === "good" ? "border-[#0066FF]/25" : "border-amber-500/25"
      } p-5`}
    >
      <div
        className={`mono text-[10px] uppercase tracking-[0.22em] pb-2 border-b ${headerCls}`}
      >
        {label}
      </div>
      <ul className="mt-3 space-y-2.5 text-sm text-slate-200 leading-relaxed">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2.5">
            <span className={`mt-2 w-1 h-1 ${dot}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ---------------------------------------------------------------- page

export default function EsrcFit() {
  const [partnersOpen, setPartnersOpen] = useState(false);
  const shareUrl =
    "https://platform.katlastechnology.com/fit/esrc-future-data-services";

  return (
    <div className="min-h-screen bg-[#020B14] text-white" id="top">
      <Nav />
      <main className="pt-24 pb-24">
        <div className="max-w-[760px] mx-auto px-5 sm:px-8">
          {/* Breadcrumb + meta */}
          <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-8 flex-wrap">
            <Link to="/" data-testid="crumb-home" className="hover:text-white">
              Platform
            </Link>
            <span>/</span>
            <Link to="/fit" data-testid="crumb-fit" className="hover:text-white">
              Calls / Challenges Fit
            </Link>
            <span>/</span>
            <span className="text-slate-200">ESRC Future Data Services</span>
            <span className="ml-auto text-slate-500">≈ 2 min read</span>
          </div>

          {/* Hero */}
          <header className="mb-10">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
              Branch · ESRC Future Data Services
            </div>
            <h1
              data-testid="esrc-headline"
              className="font-display text-[26px] sm:text-4xl mt-4 leading-[1.15]"
            >
              ESRC Future Data Services:{" "}
              <span className="text-slate-400">
                receipt-backed federated access
              </span>
            </h1>
            <p
              data-testid="esrc-subhead"
              className="mt-5 text-[15px] sm:text-base text-slate-300 leading-relaxed"
            >
              A governed access and coordination model for improving
              discovery, permissioned use and evidence-led coordination
              across existing ESRC social science data service
              infrastructures. The aim is not to create another data service,
              but to enhance existing services with role-governed access,
              bounded AI assistance, local data custody, staff upskilling
              and receipt-backed reporting.
            </p>

            <div
              data-testid="esrc-strap"
              className="mt-6 inline-flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border border-white/[0.08] bg-white/[0.02] px-3 py-2"
            >
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Federated access
              </span>
              <span className="text-slate-700" aria-hidden>·</span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Data custody
              </span>
              <span className="text-slate-700" aria-hidden>·</span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Bounded AI
              </span>
              <span className="text-slate-700" aria-hidden>·</span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Receipts
              </span>
            </div>

            <div className="mt-4 border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#EAB308]">
                Positioning note
              </div>
              <p
                data-testid="enhance-not-replace"
                className="mt-1.5 text-sm text-amber-100 leading-relaxed"
              >
                KATLAS <strong className="text-white">enhances existing data services</strong> — it
                does not propose a new infrastructure, and does not imply any
                named service has agreed to participate.
              </p>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
              <CopyButton text={SUMMARY_TEXT} label="Copy summary" testId="copy-summary-btn" />
              <CopyButton text={shareUrl} label="Copy share link" testId="copy-share-btn" />
            </div>
          </header>

          {/* Challenge summary + KATLAS fit */}
          <section className="grid grid-cols-1 gap-3.5">
            <Card
              n="ESRC"
              testId="esrc-challenge-card"
              title="ESRC challenge summary"
              body="The opportunity seeks projects that improve how researchers discover, access and use social science data; advance technology-enabled data pipelines for management, curation, discovery and access; and upskill data service staff. Projects must enhance existing ESRC data service infrastructures and avoid duplicating or creating new infrastructure."
            />
            <Card
              n="KATLAS"
              testId="esrc-katlas-card"
              title="KATLAS fit"
              body="KATLAS can lead a work package on governed federated access and researcher journey automation. Researchers, data owners, data services, ethics reviewers, TRE operators and access approvers can interact through role-to-role workflows where purpose, identity, authority, AI/tool boundaries, dependencies and outcomes are recorded."
            />
          </section>

          {/* Proposed work package */}
          <section className="mt-10" data-testid="esrc-workpackage">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
              Proposed work package
            </div>
            <h2 className="font-display text-xl sm:text-2xl mt-3 text-white leading-snug">
              Receipt-backed Federated Access and Governed AI Researcher
              Journeys
            </h2>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              Reduce friction in discovery and sensitive-data access by
              governing the researcher journey across existing ESRC data
              services — while keeping sensitive data local, bounding AI/tool
              use, supporting staff adoption, and producing signed receipts
              for access decisions, dependencies and outcomes.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-3" data-testid="wp-tasks">
              {WP_TASKS.map((t) => (
                <Card key={t.n} {...t} testId={`wp-task-${t.n}`} />
              ))}
            </div>
          </section>

          {/* Demo scenario */}
          <section className="mt-10" data-testid="esrc-demo">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
              Demo scenario
            </div>
            <h3 className="font-display text-lg sm:text-xl mt-3 text-white leading-snug">
              Linked social-determinants discovery and governed access
            </h3>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              A researcher wants to study health-adjacent social determinants
              using linked or discoverable data across place, energy, housing,
              longitudinal and administrative datasets. The demonstrator
              shows how the researcher can discover relevant data, understand
              access requirements, make a governed request, use bounded AI
              assistance, receive role-based approvals, and generate receipts
              for decisions and outputs — without raw sensitive data leaving
              custody.
            </p>
          </section>

          {/* Partner fit — collapsible to keep page short */}
          <section className="mt-10" data-testid="esrc-partners">
            <button
              type="button"
              data-testid="partners-toggle"
              aria-expanded={partnersOpen}
              onClick={() => setPartnersOpen((v) => !v)}
              className="w-full flex items-center justify-between border border-white/[0.12] hover:border-white/30 bg-[#071627] hover:bg-[#0D2035] px-5 py-4 transition-colors"
            >
              <span className="text-left">
                <span className="block mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
                  Potential infrastructure partners
                </span>
                <span className="block font-display text-base sm:text-lg text-white mt-1">
                  Illustrative — no participation implied
                </span>
              </span>
              <svg
                width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2"
                className={`text-slate-300 transition-transform ${
                  partnersOpen ? "rotate-180" : ""
                }`}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {partnersOpen && (
              <ul
                data-testid="partners-list"
                className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
              >
                {PARTNERS.map((p) => (
                  <li
                    key={p}
                    className="flex items-center gap-2.5 border border-white/[0.1] bg-[#020B14] px-3 py-2.5 text-sm text-slate-100"
                  >
                    <span className="w-1.5 h-1.5 bg-[#0066FF]" />
                    {p}
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-slate-500 italic leading-relaxed">
              Listing reflects the kind of infrastructure where governed
              federated access would be most useful. It does not represent
              consent, collaboration agreement or endorsement by any named
              organisation.
            </p>
          </section>

          {/* Qualification gate */}
          <section className="mt-10" data-testid="qualification-gate">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
              Qualification gate
            </div>
            <h2 className="font-display text-xl sm:text-2xl mt-3 text-white leading-snug">
              Good fit / Weak fit
            </h2>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FitColumn tone="good" label="Good fit if" items={GOOD_FIT} testId="good-fit" />
              <FitColumn tone="weak" label="Weak fit if" items={WEAK_FIT} testId="weak-fit" />
            </div>
          </section>

          {/* Operating line */}
          <section className="mt-10 border border-[#0066FF]/30 bg-[#0066FF]/[0.06] p-5 sm:p-6">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
              Operating line
            </div>
            <p
              data-testid="esrc-operating-line"
              className="mt-3 font-display text-lg sm:text-xl text-white leading-snug"
            >
              AI assists.
              <br className="sm:hidden" /> Roles decide.
              <br className="sm:hidden" />{" "}
              <span className="text-[#EAB308]">Receipts prove.</span>
            </p>
          </section>

          {/* CTA */}
          <section className="mt-10 border-t border-white/[0.08] pt-8">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
              Explore fit with KATLAS
            </div>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              Use this page to test whether KATLAS should lead a work
              package on governed federated access, bounded AI/tool use,
              lifecycle governance and receipt-backed reporting for ESRC
              Future Data Services.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <a
                href="mailto:edward.cole@katlastechnology.io?subject=ESRC%20Future%20Data%20Services%20walkthrough"
                data-testid="esrc-walkthrough-cta"
                className="cta-primary inline-flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white px-5 py-3 text-sm font-medium rounded-sm"
              >
                Request a walkthrough
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </a>
            </div>
          </section>

          {/* Bottom navigation */}
          <nav className="mt-12 pt-6 border-t border-white/[0.08] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <Link
              to="/fit"
              data-testid="back-fit-link"
              className="mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-white inline-flex items-center gap-2"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to Calls / Challenges Fit
            </Link>
            <Link
              to="/fit/horizon"
              data-testid="next-horizon-link"
              className="mono text-[11px] uppercase tracking-[0.2em] text-[#0066FF] hover:text-white inline-flex items-center gap-2"
            >
              Horizon Europe branch
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </Link>
          </nav>
        </div>
      </main>
      <Footer />
    </div>
  );
}
